import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/**
 * Havvok custom sticky add-to-cart.
 *
 * Behaviour:
 *  - Mobile only, visible on page load (no scroll trigger).
 *  - Rotating banner cycling 3 messages.
 *  - Single wide ATC button -> opens size popup.
 *  - Size popup: boxes matching main picker, "X left" when inventory <= 5,
 *    sold-out sizes disabled, first available size preselected.
 *  - Selecting a size keeps popup open. Tapping the popup's ADD TO CART
 *    sets the real variant + puppet-clicks the real ATC (cart behaviour
 *    unchanged), plays the theme button burst, then closes the popup.
 *  - SIZE GUIDE opens an image popup (close button + click-outside).
 *
 * @extends {Component}
 */
class StickyAddToCartComponent extends Component {
  requiredRefs = ['stickyBar', 'openButton', 'sizePopup', 'popupAddButton', 'sizeGuidePopup'];

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {number | undefined} */
  #bannerTimer;

  /** @type {number} */
  #bannerIndex = 0;

  /** @type {number} */
  #bannerCount = 0;

  /** Cached banner row height (read once). Banner height is fixed in CSS. */
  /** @type {number} */
  #bannerStep = 24;

  /** @type {number} */
  #bannerRealCount = 0;

  /** @type {boolean} */
  #bannerVisible = false;

  /** @type {IntersectionObserver | undefined} */
  #bannerIO;

  /** @type {string | null} */
  #selectedVariantId = null;

  /** @type {number | undefined} */
  #resetTimeout;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;

    // Preselect the size that is marked selected in markup (matches main picker),
    // otherwise the first available size.
    this.#initSelection();

    // Start banner rotation.
    this.#startBanner();

    // Reset puppet flag after any cart op (success or error).
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartComplete, { signal });
    document.addEventListener(ThemeEvents.cartError, this.#handleCartComplete, { signal });

    // Keep popup state sane if variant changes elsewhere on the page.
    const sectionEl = this.closest('.shopify-section');
    sectionEl?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });

    // Close popups on Escape.
    document.addEventListener('keydown', this.#handleKeydown, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.#bannerTimer) clearInterval(this.#bannerTimer);
    if (this.#resetTimeout) clearTimeout(this.#resetTimeout);
    this.#bannerIO?.disconnect();
    this.#abortController.abort();
  }

  /* ------------------------------------------------------------------ */
  /* Banner                                                              */
  /* ------------------------------------------------------------------ */

  #startBanner() {
    const track = this.refs.bannerTrack;
    if (!track) return;

    // The track contains the real messages plus one clone of the first message
    // appended at the end (added in Liquid). We advance forward 1-2-3-(clone of 1),
    // then snap back to the real first message with no transition -> seamless 1-2-3-1-2-3.
    const realCount = parseInt(this.dataset.bannerCount || '0', 10) || track.children.length - 1;
    if (realCount <= 1) return;

    // Read layout ONCE. Banner row height is fixed in CSS (24px) and never
    // changes, so we never touch offsetHeight again — no per-tick forced reflow.
    const firstMsg = /** @type {HTMLElement} */ (track.children[0]);
    this.#bannerStep = firstMsg ? firstMsg.offsetHeight : 24;
    this.#bannerRealCount = realCount;

    // Only run the timer when the banner is actually visible AND the tab is
    // active. An off-screen/backgrounded banner does zero layout work, so it
    // can never land inside a user-interaction (INP) window.
    this.#bannerIO = new IntersectionObserver((entries) => {
      this.#bannerVisible = entries[0]?.isIntersecting ?? false;
      this.#syncBannerTimer();
    }, { threshold: 0 });
    this.#bannerIO.observe(track);

    document.addEventListener('visibilitychange', this.#syncBannerTimer, {
      signal: this.#abortController.signal,
    });

    this.#syncBannerTimer();
  }

  /** Starts/stops the banner interval based on visibility + tab state. */
  #syncBannerTimer = () => {
    const shouldRun = this.#bannerVisible && document.visibilityState === 'visible';
    if (shouldRun && !this.#bannerTimer) {
      this.#bannerTimer = setInterval(this.#bannerTick, 2500);
    } else if (!shouldRun && this.#bannerTimer) {
      clearInterval(this.#bannerTimer);
      this.#bannerTimer = undefined;
    }
  };

  /** One banner advance. No layout reads — uses the cached step height. */
  #bannerTick = () => {
    const track = this.refs.bannerTrack;
    if (!track) return;

    this.#bannerIndex += 1;
    track.style.transition = '';
    track.style.transform = `translateY(-${this.#bannerIndex * this.#bannerStep}px)`;

    // When we've animated onto the clone (index === realCount), wait for the
    // slide to finish, then jump back to index 0 instantly (no transition).
    if (this.#bannerIndex === this.#bannerRealCount) {
      setTimeout(() => {
        track.style.transition = 'none';
        this.#bannerIndex = 0;
        track.style.transform = 'translateY(0)';
        // Re-enable the transition on a later frame using rAF instead of a
        // forced `offsetHeight` reflow — keeps this off the layout path.
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            track.style.transition = '';
          });
        });
      }, 550); // must be >= the CSS transition duration (0.5s)
    }
  };

  /* ------------------------------------------------------------------ */
  /* Selection helpers                                                   */
  /* ------------------------------------------------------------------ */

  #initSelection() {
    const list = this.refs.sizeList;
    if (!list) return;

    const preselected = list.querySelector('.hvk-size.is-selected:not(.is-soldout)');
    if (preselected) {
      this.#selectedVariantId = preselected.getAttribute('data-variant-id');
      return;
    }

    // No preselected (or it's sold out) -> pick first available.
    const firstAvailable = list.querySelector('.hvk-size[data-available="true"]');
    if (firstAvailable) {
      this.#markSelected(firstAvailable);
      this.#selectedVariantId = firstAvailable.getAttribute('data-variant-id');
    } else {
      this.#selectedVariantId = null;
    }
  }

  /** @param {Element} el */
  #markSelected(el) {
    const list = this.refs.sizeList;
    if (!list) return;
    list.querySelectorAll('.hvk-size').forEach((b) => b.classList.remove('is-selected'));
    el.classList.add('is-selected');
  }

  /* ------------------------------------------------------------------ */
  /* Size popup                                                          */
  /* ------------------------------------------------------------------ */

  /** Opens the size popup. */
  handleOpenClick = () => {
    if (this.refs.openButton?.disabled) return;
    this.#openSizePopup();
  };

  #openSizePopup() {
    const { sizePopup } = this.refs;
    if (!sizePopup) return;
    sizePopup.dataset.open = 'true';
    document.documentElement.style.overflow = 'hidden';
  }

  /** Closes the size popup. */
  closeSizePopup = () => {
    const { sizePopup } = this.refs;
    if (!sizePopup) return;
    sizePopup.dataset.open = 'false';
    this.#restoreScrollIfNoPopups();
  };

  /**
   * Handle tapping a size box. Only updates selection; popup stays open.
   * @param {Event} event
   */
  handleSizeSelect = (event) => {
    // The theme's event system remaps event.target to the element carrying the
    // on:click attribute (the .hvk-size button), and does NOT set currentTarget.
    const raw = /** @type {Element | null} */ (event.target);
    const target = /** @type {HTMLElement | null} */ (raw?.closest ? raw.closest('.hvk-size') : raw);
    if (!target || target.getAttribute('data-available') !== 'true') return;
    this.#markSelected(target);
    this.#selectedVariantId = target.getAttribute('data-variant-id');
  };

  /* ------------------------------------------------------------------ */
  /* Add to cart from popup                                              */
  /* ------------------------------------------------------------------ */

  handlePopupAdd = async () => {
    if (!this.#selectedVariantId) return;

    const productForm = this.#getProductForm();
    if (!productForm) return;

    const targetButton = /** @type {HTMLButtonElement | null} */ (
      productForm.querySelector('[ref="addToCartButton"]')
    );
    if (!targetButton) return;

    // 1. Point the real product form at the chosen variant.
    const hiddenId = /** @type {HTMLInputElement | null} */ (productForm.querySelector('input[name="id"]'));
    if (hiddenId) hiddenId.value = this.#selectedVariantId;

    // 2. Keep the URL variant param in sync (product-form reads this first on
    //    the queued path) so both code paths resolve the same variant.
    try {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', this.#selectedVariantId);
      history.replaceState({}, '', url.toString());
    } catch (e) {
      /* no-op */
    }

    // 3. Puppet-click the real ATC (reuses theme's add-to-cart + cart drawer).
    targetButton.dataset.puppet = 'true';
    targetButton.click();

    // 4. Close the size popup, then play the burst on the STICKY BAR button.
    this.closeSizePopup();
    this.#playAddedFeedback();

    // Safety: clear puppet flag even if no cart event fires.
    if (this.#resetTimeout) clearTimeout(this.#resetTimeout);
    this.#resetTimeout = setTimeout(() => {
      if (targetButton) targetButton.dataset.puppet = 'false';
    }, 2000);
  };

  

  #playAddedFeedback() {
    const btn = this.refs.openButton;
    if (!btn) return;

    // Burst animation on the sticky bar button.
    btn.dataset.added = 'true';
    setTimeout(() => {
      btn.removeAttribute('data-added');
    }, 1000);
  }
    

  /* ------------------------------------------------------------------ */
  /* Size guide popup                                                    */
  /* ------------------------------------------------------------------ */

  openSizeGuide = () => {
    const { sizeGuidePopup } = this.refs;
    if (!sizeGuidePopup) return;
    sizeGuidePopup.dataset.open = 'true';
    document.documentElement.style.overflow = 'hidden';
  };

  closeSizeGuide = () => {
    const { sizeGuidePopup } = this.refs;
    if (!sizeGuidePopup) return;
    sizeGuidePopup.dataset.open = 'false';
    this.#restoreScrollIfNoPopups();
  };

  /* ------------------------------------------------------------------ */
  /* Misc                                                                */
  /* ------------------------------------------------------------------ */

  #restoreScrollIfNoPopups() {
    const sizeOpen = this.refs.sizePopup?.dataset.open === 'true';
    const guideOpen = this.refs.sizeGuidePopup?.dataset.open === 'true';
    if (!sizeOpen && !guideOpen) {
      document.documentElement.style.overflow = '';
    }
  }

  /** @param {KeyboardEvent} event */
  #handleKeydown = (event) => {
    if (event.key !== 'Escape') return;
    if (this.refs.sizeGuidePopup?.dataset.open === 'true') {
      this.closeSizeGuide();
    } else if (this.refs.sizePopup?.dataset.open === 'true') {
      this.closeSizePopup();
    }
  };

  /** @param {CustomEvent} _event */
  #handleCartComplete = (_event) => {
    const productForm = this.#getProductForm();
    const targetButton = productForm?.querySelector('[ref="addToCartButton"]');
    if (targetButton instanceof HTMLElement) targetButton.dataset.puppet = 'false';
  };

  /**
   * If the variant changes on the main PDP, mirror that selection in the popup.
   * @param {CustomEvent} event
   */
  #handleVariantUpdate = (event) => {
    const variant = event.detail?.resource;
    if (!variant || !variant.id) return;
    const list = this.refs.sizeList;
    if (!list) return;
    const match = list.querySelector(`.hvk-size[data-variant-id="${variant.id}"]`);
    if (match && match.getAttribute('data-available') === 'true') {
      this.#markSelected(match);
      this.#selectedVariantId = String(variant.id);
    }
  };

  /** @returns {HTMLElement | null} */
  #getProductForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;
    const sectionElement = this.closest('.shopify-section');
    if (!sectionElement) return null;
    return sectionElement.querySelector(`product-form-component[data-product-id="${productId}"]`);
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCartComponent);
}